import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useCallback } from '@web-stories-wp/react';
import {
  NumericInput,
  Text,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';
import { __, _x } from '@web-stories-wp/i18n';

import {
  BACKGROUND_TEXT_MODE,
  MULTIPLE_DISPLAY_VALUE,
  MULTIPLE_VALUE,
} from '../../../../constants';
import getCommonValue from '../../shared/getCommonValue';
import { inputContainerStyleOverride } from '../../shared/styles';
import Row from '../../../form/row';
import {
  normalizeTextBoxBackdropValue,
  TEXT_BOX_BACKDROP_MIN,
} from '../../../../elements/text/getTextBoxBackdropStyles';
import { getElementBlendMode } from '../../../../elements/shared/blendModes';
import { supportsElementBackdrop } from '../../../../elements/shared/getElementBackdropStyles';

const Space = styled.div`
  flex: 0 0 8px;
`;

const BackdropControlsGroup = styled.div`
  margin-bottom: 16px;
`;

const BackdropInputsRow = styled(Row)`
  margin-bottom: 0;
`;

const HelperText = styled(Text).attrs({
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL,
})`
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.fg.tertiary};
`;

const getBackdropValue = (property) => (element) =>
  normalizeTextBoxBackdropValue(element[property]);

function BackdropControls({
  selectedElements,
  pushUpdate,
  isElementBackdrop = false,
}) {
  const blurProperty = isElementBackdrop
    ? 'backdropBlur'
    : 'textBoxBackdropBlur';
  const softnessProperty = isElementBackdrop
    ? 'backdropEdgeSoftness'
    : 'textBoxBackdropEdgeSoftness';
  const supportsBackdrop = selectedElements.every((element) =>
    isElementBackdrop
      ? supportsElementBackdrop(element)
      : element.backgroundTextMode === BACKGROUND_TEXT_MODE.NONE ||
        element.backgroundTextMode === BACKGROUND_TEXT_MODE.FILL
  );
  const blur = getCommonValue(selectedElements, getBackdropValue(blurProperty));
  const edgeSoftness = getCommonValue(
    selectedElements,
    getBackdropValue(softnessProperty)
  );
  const blurDisabled =
    !isElementBackdrop &&
    selectedElements.some(({ blendMode }) =>
      Boolean(getElementBlendMode(blendMode))
    );
  const handleChange = useCallback(
    (property, value, submit) => {
      pushUpdate({ [property]: normalizeTextBoxBackdropValue(value) }, submit);
    },
    [pushUpdate]
  );

  if (!supportsBackdrop) {
    return null;
  }

  const inputProps = {
    min: TEXT_BOX_BACKDROP_MIN,
    unit: _x('px', 'Pixels', 'web-stories'),
    containerStyleOverride: inputContainerStyleOverride,
  };

  return (
    <BackdropControlsGroup
      role="group"
      aria-label={__('Backdrop effects', 'web-stories')}
    >
      <BackdropInputsRow>
        <NumericInput
          {...inputProps}
          suffix={__('Blur', 'web-stories')}
          aria-label={
            isElementBackdrop
              ? __('Background blur amount in pixels', 'web-stories')
              : __('Blur amount in pixels', 'web-stories')
          }
          value={blur}
          placeholder={blur === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : null}
          isIndeterminate={blur === MULTIPLE_VALUE}
          disabled={blurDisabled}
          onChange={(evt, value) => handleChange(blurProperty, value, false)}
          onBlur={(evt, value) => handleChange(blurProperty, value, true)}
        />
        <Space />
        <NumericInput
          {...inputProps}
          suffix={__('Softness', 'web-stories')}
          aria-label={__('Edge softness in pixels', 'web-stories')}
          value={edgeSoftness}
          placeholder={
            edgeSoftness === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : null
          }
          isIndeterminate={edgeSoftness === MULTIPLE_VALUE}
          disabled={
            (isElementBackdrop && blurDisabled) ||
            (blur !== MULTIPLE_VALUE && blur <= 0)
          }
          onChange={(evt, value) =>
            handleChange(softnessProperty, value, false)
          }
          onBlur={(evt, value) => handleChange(softnessProperty, value, true)}
        />
      </BackdropInputsRow>
      {blurDisabled ? (
        <HelperText>
          {__('Blur is only available with Normal blend mode.', 'web-stories')}
        </HelperText>
      ) : null}
    </BackdropControlsGroup>
  );
}

BackdropControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
  isElementBackdrop: PropTypes.bool,
};

export default BackdropControls;
